import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Button, message, Spin, Radio } from 'antd';
import { graphql, compose } from 'react-apollo';
import localForage from 'localforage';
import MuiIconButton from 'material-ui/IconButton';
import MuiButton from 'material-ui/Button';
import DeleteIcon from '@material-ui/icons/Delete';

import QUERY from '../../graphql/queries/createLeadQueries.graphql';
import MUTATION from '../../graphql/mutations/newLeadMutation.graphql';
import ALL_QUERY from '../../graphql/queries/allLeadsQuery.graphql';

import { ratings } from '../../utils/lists';
import { currencyFormat, newCurrentProposal, amountUnitDetails, leadFinancialSummaryNames } from '../../utils/globals';

import BizSelect from '../../components/UI/BizSelect';
import BizCard from '../../components/UI/BizCard';
import BizInput from '../../components/UI/BizInput';
import FormFloatingButtons from '../../components/FormFloatingButtons';
import AssociateCreateDialog from './AssociateCreateDialog';

const RadioGroup = Radio.Group;

const DeleteFormRowButton = (props) => (
  <MuiIconButton
    aria-label="Delete"
    onClick={props.onClick}
    style={{ width: 30, height: 30 }}
  >
    <DeleteIcon />
  </MuiIconButton>
);
DeleteFormRowButton.propTypes = {
  onClick: PropTypes.func.isRequired,
};

const initialState = () => ({
  name: null,
  website: null,
  landline: null,
  companyEmail: null,
  constitution: null,
  sectors: [],
  activities: [],
  groupName: null,
  segment: null,
  processingLocation: null,
  addresses: [],
  promoters: [],
  externalRatings: [],
  existingBankers: [],
  currentProposal: [],
  amountUnit: 'crore',
  proposalType: null,
  proposalBrief: null,
  financialSummaryYears: new Array(3),
  financialSummary: {
    sales: [0, 0, 0],
    otherIncome: [0, 0, 0],
    netProfit: [0, 0, 0],
    interestExpLoans: [0, 0, 0],
    interestExpOthers: [0, 0, 0],
    depreciation: [0, 0, 0],
    tax: [0, 0, 0],
    ebidta: [0, 0, 0],
    capital: [0, 0, 0],
    unsecuredLoans: [0, 0, 0],
    longTermLoans: [0, 0, 0],
    shortTermLoans: [0, 0, 0],
    tolByTnw: [0, 0, 0],
    currentRatio: [0, 0, 0],
  },
  contacts: [],
  projectOwnerId: null,
  referredByAssociateId: null,
  referredByEmployeeId: null,
  startedAt: new Date().toISOString(),
});

const localForageItemkey = 'newLeadSavedData';

const currentYear = new Date().getFullYear();
const yearsList = [];
for (let i = 7; i >= 0; i--) {
  yearsList.push(currentYear - i);
}

class LeadCreate extends Component {
  state = {
    ...initialState(),
    additionalStates: {
      associateCreateDialog: false,
    },
  };

  componentDidMount() {
    localForage.getItem(localForageItemkey)
      .then((value) => {
        if (value) {
          this.setState({
            ...value,
          });
        }
      });
  }

  clearForm = () => {
    localForage.setItem(localForageItemkey, null);
    this.setState({
      ...initialState(),
    });
  };

  saveData = () => {
    const { ...data } = this.state;
    localForage.setItem(localForageItemkey, data)
      .then(() => {
        message.success('Saved!');
      })
      .catch(() => {
        message.error('Not Saved!');
      });
  };

  createOptionsFromList = (list) => list.map((entry) => ({ label: entry, value: entry }));

  handleFormValue = (key, value) => {
    this.setState({
      [key]: value,
    });
  };

  handleArrayFormValue = (arrayName, id, key, value) => {
    this.setState({
      [arrayName]: this.state[arrayName].map((entry) => {
        const newEntry = entry;
        if (newEntry.id === id) newEntry[key] = value;
        return newEntry;
      }),
    });
  };

  removeFormRow = (stateObjKey, id) => {
    this.setState({
      [stateObjKey]: this.state[stateObjKey].filter((entry) => (entry.id !== id)),
    });
  };

  handleDialog = (key, state) => {
    this.setState({
      additionalStates: {
        ...this.state.additionalStates,
        [key]: state,
      },
    });
  };

  handleFormSubmit = () => {
    const { additionalStates, ...variables } = this.state;

    this.props.mutate({
      variables,
      update: (cache, { data: { newLead } }) => {
        const data = cache.readQuery({
          query: ALL_QUERY,
        });
        if (!Array.isArray(data.leads)) data.leads = [];
        data.leads.push(newLead);
        cache.writeQuery({ query: ALL_QUERY, data });
      },
    })
      .then(() => {
        message.success('Success!');
        localForage.setItem(localForageItemkey, null);
        this.setState({
          ...initialState(),
        });
      })
      .catch((mutateErr) => {
        message.error(mutateErr.message);
      });
  };

  render() {
    const {
      name,
      constitution,
      sectors,
      activities,
      groupName,
      segment,
      processingLocation,
      addresses,
      promoters,
      externalRatings,
      existingBankers,
      currentProposal,
      contacts,
      landline,
      companyEmail,
      website,
      projectOwnerId,
      proposalBrief,
      amountUnit,
      proposalType,
      financialSummaryYears,
      financialSummary,
      referredByEmployeeId,
      referredByAssociateId,

      additionalStates,
    } = this.state;

    const { loading, employees, lists, associates } = this.props.data;

    if (loading) {
      return <Spin size="large" />;
    }

    let totalExistingBankers = 0;
    existingBankers.forEach((entry) => {
      totalExistingBankers += entry.tl + entry.cc + entry.bg + entry.lc + entry.other;
    });

    return (
      <BizCard title="Create new Lead">
        <AssociateCreateDialog open={additionalStates.associateCreateDialog} handleState={this.handleDialog} />
        <FormFloatingButtons handleSave={this.saveData} handleClear={this.clearForm} />
        <table className="biz-form-table">
          <tbody>
            {/* Amount Units */}
            <tr>
              <td className="biz-form-table-label">Global unit for amount</td>
              <td className="biz-form-table-input">
                <RadioGroup
                  value={amountUnit}
                  onChange={(e) => { this.setState({ amountUnit: e.target.value }); }}
                >
                  <Radio value="lakh">Lakhs</Radio>
                  <Radio value="crore">Crores</Radio>
                  <Radio value="million">Millions</Radio>
                </RadioGroup>
              </td>
              <td className="biz-form-table-action" />
            </tr>

            {/* Name of the lead */}
            <tr>
              <td className="biz-form-table-label">Name of the Lead</td>
              <td className="biz-form-table-input">
                <BizInput name="name" theme="lac" onChange={(value) => { this.setState({ name: value }); }} value={name} />
              </td>
              <td className="biz-form-table-action" />
            </tr>

            {/* Constitution */}
            <tr>
              <td className="biz-form-table-label">Constitution</td>
              <td className="biz-form-table-input">
                <BizSelect
                  options={this.createOptionsFromList(lists.constitutions)}
                  theme="lac"
                  onChange={(value) => { this.setState({ constitution: value }); }}
                  name="constitution"
                  value={constitution}
                />
              </td>
              <td className="biz-form-table-action" />
            </tr>

            {/* Sector */}
            <tr>
              <td className="biz-form-table-label">Sectors</td>
              <td className="biz-form-table-input">
                <BizSelect
                  options={this.createOptionsFromList(lists.sectors)}
                  theme="lac"
                  onChange={(value) => { this.setState({ sectors: value }); }}
                  name="sector"
                  value={sectors}
                  multi
                />
              </td>
              <td className="biz-form-table-action" />
            </tr>

            {/* Activity */}
            <tr>
              <td className="biz-form-table-label">Activities</td>
              <td className="biz-form-table-input">
                <BizSelect
                  options={this.createOptionsFromList(lists.activities)}
                  theme="lac"
                  onChange={(value) => { this.setState({ activities: value }); }}
                  name="activities"
                  value={activities}
                  multi
                />
              </td>
              <td className="biz-form-table-action" />
            </tr>

            {/* Segment */}
            <tr>
              <td className="biz-form-table-label">Segment</td>
              <td className="biz-form-table-input">
                <BizSelect
                  options={this.createOptionsFromList(lists.segments)}
                  theme="lac"
                  name="segment"
                  onChange={(value) => { this.setState({ segment: value }); }}
                  value={segment}
                />
              </td>
              <td className="biz-form-table-action" />
            </tr>

            {/* Group name */}
            <tr>
              <td className="biz-form-table-label">Group Name</td>
              <td className="biz-form-table-input">
                <BizInput
                  name="groupName"
                  theme="lac"
                  onChange={(value) => { this.setState({ groupName: value }); }}
                  value={groupName}
                />
              </td>
              <td className="biz-form-table-action" />
            </tr>

            {/* Proposal Type */}
            <tr>
              <td className="biz-form-table-label">Proposal Type</td>
              <td className="biz-form-table-input">
                <RadioGroup
                  value={proposalType}
                  onChange={(e) => { this.setState({ proposalType: e.target.value }); }}
                >
                  <Radio value="greenfield">Greenfield</Radio>
                  <Radio value="brownfieldNew">Brownfield - New</Radio>
                  <Radio value="brownfieldExpansion">Brownfield - Expansion</Radio>
                </RadioGroup>
              </td>
              <td className="biz-form-table-action" />
            </tr>

            {/* Processing location */}
            <tr>
              <td className="biz-form-table-label">Processing Location</td>
              <td className="biz-form-table-input">
                <BizInput
                  name="processingLocation"
                  theme="lac"
                  onChange={(value) => { this.setState({ processingLocation: value }); }}
                  value={processingLocation}
                />
              </td>
              <td className="biz-form-table-action" />
            </tr>

            {/* Addresses */}
            <tr>
              <td className="biz-form-table-label">Address</td>
              <td className="biz-form-table-input">
                {
                  addresses.length > 0 ?
                    <table className="biz-data-table">
                      <thead>
                        <tr>
                          <th>Type</th>
                          <th>Street address</th>
                          <th>City</th>
                          <th>State</th>
                          <th>Pincode</th>
                          <th />
                        </tr>
                      </thead>
                      <tbody>
                        {
                          addresses.map((address) => (
                            <tr key={address.id}>
                              <td style={{ width: 120 }}>
                                <BizSelect
                                  options={this.createOptionsFromList(lists.addressTypes)}
                                  theme="lac"
                                  name={`address[${address.id}][type]`}
                                  onChange={(value) => { this.handleArrayFormValue('addresses', address.id, 'type', value); }}
                                  value={address.type}
                                />
                              </td>
                              <td>
                                <BizInput
                                  type="textarea"
                                  name={`address[${address.id}][address]`}
                                  theme="lac"
                                  onChange={(value) => { this.handleArrayFormValue('addresses', address.id, 'address', value); }}
                                  value={address.address}
                                />
                              </td>
                              <td>
                                <BizInput
                                  name={`address[${address.id}][city]`}
                                  theme="lac"
                                  onChange={(value) => { this.handleArrayFormValue('addresses', address.id, 'city', value); }}
                                  value={address.city}
                                />
                              </td>
                              <td>
                                <BizInput
                                  name={`address[${address.id}][state]`}
                                  theme="lac"
                                  onChange={(value) => { this.handleArrayFormValue('addresses', address.id, 'state', value); }}
                                  value={address.state}
                                />
                              </td>
                              <td>
                                <BizInput
                                  name={`address[${address.id}][pincode]`}
                                  theme="lac"
                                  onChange={(value) => { this.handleArrayFormValue('addresses', address.id, 'pincode', value); }}
                                  value={address.pincode}
                                />
                              </td>
                              <td>
                                <DeleteFormRowButton onClick={() => { this.removeFormRow('addresses', address.id); }} />
                              </td>
                            </tr>
                          ))
                        }
                      </tbody>
                    </table>
                    :
                    null
                }
              </td>
              <td className="biz-form-table-action">
                <Button
                  onClick={() => {
                    this.setState({
                      addresses: [...addresses, {
                        id: (+new Date()).toString(36),
                        type: null,
                        address: '',
                        city: '',
                        state: '',
                        pincode: '',
                      }],
                    });
                  }}
                >
                  Add new address
                </Button>
              </td>
            </tr>

            {/* Name of promoters */}
            <tr>
              <td className="biz-form-table-label">Promoters</td>
              <td className="biz-form-table-input">
                {
                  promoters.length > 0 ?
                    <table className="biz-data-table">
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>PAN</th>
                          <th>DOB</th>
                          <th>Street address</th>
                          <th>City, State & Pincode</th>
                          <th>Net Worth</th>
                          <th />
                        </tr>
                      </thead>
                      <tbody>
                        {
                          promoters.map((entry) => (
                            <tr key={entry.id}>
                              <td>
                                <BizInput
                                  theme="lac"
                                  name={`promoters[${entry.id}][firstName]`}
                                  onChange={(value) => { this.handleArrayFormValue('promoters', entry.id, 'firstName', value); }}
                                  value={entry.firstName}
                                  placeholder="First name"
                                  style={{ display: 'block', marginBottom: 3 }}
                                />
                                <BizInput
                                  theme="lac"
                                  name={`promoters[${entry.id}][middleName]`}
                                  onChange={(value) => { this.handleArrayFormValue('promoters', entry.id, 'middleName', value); }}
                                  value={entry.middleName}
                                  placeholder="Middle name"
                                  style={{ display: 'block', marginBottom: 3 }}
                                />
                                <BizInput
                                  theme="lac"
                                  name={`promoters[${entry.id}][lastName]`}
                                  onChange={(value) => { this.handleArrayFormValue('promoters', entry.id, 'lastName', value); }}
                                  value={entry.lastName}
                                  placeholder="Last name"
                                />
                              </td>
                              <td>
                                <BizInput
                                  theme="lac"
                                  name={`promoters[${entry.id}][pan]`}
                                  onChange={(value) => { this.handleArrayFormValue('promoters', entry.id, 'pan', value); }}
                                  value={entry.pan}
                                />
                              </td>
                              <td>
                                <BizInput
                                  theme="lac"
                                  name={`promoters[${entry.id}][dob]`}
                                  onChange={(value) => { this.handleArrayFormValue('promoters', entry.id, 'dob', value); }}
                                  value={entry.dob}
                                  placeholder="YYYY-MM-DD"
                                />
                              </td>
                              <td>
                                <BizInput
                                  type="textarea"
                                  theme="lac"
                                  name={`promoters[${entry.id}][streetAddress]`}
                                  onChange={(value) => { this.handleArrayFormValue('promoters', entry.id, 'streetAddress', value); }}
                                  value={entry.streetAddress}
                                />
                              </td>
                              <td>
                                <BizInput
                                  theme="lac"
                                  name={`promoters[${entry.id}][city]`}
                                  onChange={(value) => { this.handleArrayFormValue('promoters', entry.id, 'city', value); }}
                                  value={entry.city}
                                  placeholder="City"
                                  style={{ display: 'block', marginBottom: 3 }}
                                />
                                <BizInput
                                  theme="lac"
                                  name={`promoters[${entry.id}][state]`}
                                  onChange={(value) => { this.handleArrayFormValue('promoters', entry.id, 'state', value); }}
                                  value={entry.state}
                                  placeholder="State"
                                  style={{ display: 'block', marginBottom: 3 }}
                                />
                                <BizInput
                                  theme="lac"
                                  name={`promoters[${entry.id}][pincode]`}
                                  onChange={(value) => { this.handleArrayFormValue('promoters', entry.id, 'pincode', value); }}
                                  value={entry.pincode}
                                  placeholder="Pincode"
                                />
                              </td>
                              <td>
                                <BizInput
                                  type="number"
                                  theme="lac"
                                  name={`promoters[${entry.id}][netWorth]`}
                                  onChange={(value) => { this.handleArrayFormValue('promoters', entry.id, 'netWorth', value); }}
                                  value={entry.netWorth}
                                />
                              </td>
                              <td>
                                <DeleteFormRowButton onClick={() => { this.removeFormRow('promoters', entry.id); }} />
                              </td>
                            </tr>
                          ))
                        }
                      </tbody>
                    </table>
                    :
                    null
                }
              </td>
              <td className="biz-form-table-action">
                <Button
                  onClick={() => {
                    this.setState({
                      promoters: [...this.state.promoters, {
                        id: (+new Date()).toString(36),
                        firstName: '',
                        middleName: '',
                        lastName: '',
                        pan: '',
                        dob: '',
                        streetAddress: '',
                        city: '',
                        state: '',
                        pincode: '',
                        netWorth: 0,
                      }],
                    });
                  }}
                >
                  Add new promoter
                </Button>
              </td>
            </tr>

            {/* Existing External Ratings */}
            <tr>
              <td className="biz-form-table-label">Existing External Ratings</td>
              <td className="biz-form-table-input">
                {
                  externalRatings.length > 0 ?
                    <table className="biz-data-table">
                      <thead>
                        <tr>
                          <th>Agency</th>
                          <th>Rating</th>
                          <th />
                        </tr>
                      </thead>
                      <tbody>
                        {
                          externalRatings.map((entry) => (
                            <tr key={entry.id}>
                              <td>
                                <BizSelect
                                  options={this.createOptionsFromList(lists.ratingAgencies)}
                                  theme="lac"
                                  name={`externalRatings[${entry.id}][agency]`}
                                  onChange={(value) => { this.handleArrayFormValue('externalRatings', entry.id, 'agency', value); }}
                                  value={entry.agency}
                                />
                              </td>
                              <td>
                                <BizSelect
                                  options={this.createOptionsFromList(Object.keys(ratings))}
                                  theme="lac"
                                  name={`externalRatings[${entry.id}][rating]`}
                                  onChange={(value) => { this.handleArrayFormValue('externalRatings', entry.id, 'rating', value); }}
                                  value={entry.rating}
                                />
                              </td>
                              <td>
                                <DeleteFormRowButton onClick={() => { this.removeFormRow('externalRatings', entry.id); }} />
                              </td>
                            </tr>
                          ))
                        }
                      </tbody>
                    </table>
                    :
                    null
                }
              </td>
              <td className="biz-form-table-action">
                <Button
                  onClick={() => {
                    this.setState({ externalRatings: [...externalRatings, { id: (+new Date()).toString(36), agency: null, rating: null }] });
                  }}
                >
                  Add new rating
                </Button>
              </td>
            </tr>

            {/* Existing Banker */}
            <tr>
              <td className="biz-form-table-label">Existing Banker</td>
              <td className="biz-form-table-input">
                <table>
                  <tbody>
                    {
                      existingBankers.map((entry) => (
                        <tr key={entry.id}>
                          <td style={{ width: 300 }}>
                            <BizSelect
                              options={this.createOptionsFromList(lists.banks)}
                              theme="lac"
                              name={`existingBankers[${entry.id}][bankName]`}
                              onChange={(value) => { this.handleArrayFormValue('existingBankers', entry.id, 'bankName', value); }}
                              value={entry.bankName}
                            />
                          </td>
                          <td>
                            <DeleteFormRowButton onClick={() => { this.removeFormRow('existingBankers', entry.id); }} />
                          </td>
                        </tr>
                      ))
                    }
                  </tbody>
                </table>
              </td>
              <td className="biz-form-table-action">
                <Button
                  onClick={() => {
                    this.setState({
                      existingBankers: [...existingBankers, {
                        id: (+new Date()).toString(36),
                        bankName: '',
                        tl: 0,
                        cc: 0,
                        bg: 0,
                        lc: 0,
                        other: 0,
                      }],
                    });
                  }}
                >
                  Add new banker
                </Button>
              </td>
            </tr>

            {/* Exposure Chart */}
            <tr>
              <td className="biz-form-table-label">Exposure Chart</td>
              <td className="biz-form-table-input">
                {
                  existingBankers.length > 0 ?
                    <div>
                      <p>The unit of amount is - {amountUnitDetails[amountUnit].name}</p>
                      <table className="biz-data-table">
                        <thead>
                          <tr>
                            <th>Bank</th>
                            <th>CC</th>
                            <th>TL</th>
                            <th>LC</th>
                            <th>BG</th>
                            <th>Other</th>
                            <th>Total</th>
                            <th>% share</th>
                          </tr>
                        </thead>
                        <tbody>
                          {
                            existingBankers.map((entry) => (
                              <tr key={entry.id}>
                                <td style={{ minWidth: 200 }}>{entry.bankName}</td>
                                <td>
                                  <BizInput
                                    type="number"
                                    theme="lac"
                                    name={`existingBankers[${entry.id}][cc]`}
                                    onChange={(value) => { this.handleArrayFormValue('existingBankers', entry.id, 'cc', value); }}
                                    value={entry.cc}
                                  />
                                </td>
                                <td>
                                  <BizInput
                                    type="number"
                                    theme="lac"
                                    name={`existingBankers[${entry.id}][tl]`}
                                    onChange={(value) => { this.handleArrayFormValue('existingBankers', entry.id, 'tl', value); }}
                                    value={entry.tl}
                                  />
                                </td>
                                <td>
                                  <BizInput
                                    type="number"
                                    theme="lac"
                                    name={`existingBankers[${entry.id}][lc]`}
                                    onChange={(value) => { this.handleArrayFormValue('existingBankers', entry.id, 'lc', value); }}
                                    value={entry.lc}
                                  />
                                </td>
                                <td>
                                  <BizInput
                                    type="number"
                                    theme="lac"
                                    name={`existingBankers[${entry.id}][bg]`}
                                    onChange={(value) => { this.handleArrayFormValue('existingBankers', entry.id, 'bg', value); }}
                                    value={entry.bg}
                                  />
                                </td>
                                <td>
                                  <BizInput
                                    type="number"
                                    theme="lac"
                                    name={`existingBankers[${entry.id}][other]`}
                                    onChange={(value) => { this.handleArrayFormValue('existingBankers', entry.id, 'other', value); }}
                                    value={entry.other}
                                  />
                                </td>
                                <td>
                                  {currencyFormat(entry.tl + entry.cc + entry.bg + entry.lc + entry.other)} {amountUnitDetails[amountUnit].symbol}
                                </td>
                                <td>
                                  {(((entry.tl + entry.cc + entry.bg + entry.lc + entry.other) / totalExistingBankers) * 100).toFixed(2)}
                                </td>
                              </tr>
                            ))
                          }
                        </tbody>
                      </table>
                    </div>
                    :
                    null
                }
              </td>
              <td className="biz-form-table-action" />
            </tr>

            {/* Current Proposal */}
            <tr>
              <td className="biz-form-table-label">Current Proposal</td>
              <td className="biz-form-table-input">
                {
                  currentProposal.length > 0 ?
                    <div>
                      <p>The unit of amount is - {amountUnitDetails[amountUnit].name}</p>
                      <table className="biz-data-table">
                        <thead>
                          <tr>
                            <th>Remark</th>
                            <th>CC</th>
                            <th>TL</th>
                            <th>LC</th>
                            <th>BG</th>
                            <th>Other</th>
                            <th>Total</th>
                            <th />
                          </tr>
                        </thead>
                        <tbody>
                          {
                            currentProposal.map((entry) => (
                              <tr key={entry.id}>
                                <td style={{ minWidth: 200 }}>
                                  <BizInput
                                    theme="lac"
                                    name={`currentProposal[${entry.id}][remark]`}
                                    onChange={(value) => { this.handleArrayFormValue('currentProposal', entry.id, 'remark', value); }}
                                    value={entry.remark}
                                  />
                                </td>
                                <td>
                                  <BizInput
                                    type="number"
                                    theme="lac"
                                    name={`currentProposal[${entry.id}][cc]`}
                                    onChange={(value) => { this.handleArrayFormValue('currentProposal', entry.id, 'cc', value); }}
                                    value={entry.cc}
                                  />
                                </td>
                                <td>
                                  <BizInput
                                    type="number"
                                    theme="lac"
                                    name={`currentProposal[${entry.id}][tl]`}
                                    onChange={(value) => { this.handleArrayFormValue('currentProposal', entry.id, 'tl', value); }}
                                    value={entry.tl}
                                  />
                                </td>
                                <td>
                                  <BizInput
                                    type="number"
                                    theme="lac"
                                    name={`currentProposal[${entry.id}][lc]`}
                                    onChange={(value) => { this.handleArrayFormValue('currentProposal', entry.id, 'lc', value); }}
                                    value={entry.lc}
                                  />
                                </td>
                                <td>
                                  <BizInput
                                    type="number"
                                    theme="lac"
                                    name={`currentProposal[${entry.id}][bg]`}
                                    onChange={(value) => { this.handleArrayFormValue('currentProposal', entry.id, 'bg', value); }}
                                    value={entry.bg}
                                  />
                                </td>
                                <td>
                                  <BizInput
                                    type="number"
                                    theme="lac"
                                    name={`currentProposal[${entry.id}][other]`}
                                    onChange={(value) => { this.handleArrayFormValue('currentProposal', entry.id, 'other', value); }}
                                    value={entry.other}
                                  />
                                </td>
                                <td>
                                  {currencyFormat(entry.tl + entry.cc + entry.bg + entry.lc + entry.other)} {amountUnitDetails[amountUnit].symbol}
                                </td>
                                <td>
                                  <DeleteFormRowButton onClick={() => { this.removeFormRow('currentProposal', entry.id); }} />
                                </td>
                              </tr>
                            ))
                          }
                        </tbody>
                      </table>
                    </div>
                    :
                    null
                }
              </td>
              <td className="biz-form-table-action">
                <Button
                  onClick={() => {
                    this.setState({
                      currentProposal: [...currentProposal, newCurrentProposal()],
                    });
                  }}
                >
                  Add new proposal
                </Button>
              </td>
            </tr>

            {/* Contact People */}
            <tr>
              <td className="biz-form-table-label">Contact People</td>
              <td className="biz-form-table-input">
                {
                  contacts.length > 0 ?
                    <table className="biz-data-table">
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Mobile</th>
                          <th>Email</th>
                          <th>Designation</th>
                          <th />
                        </tr>
                      </thead>
                      <tbody>
                        {
                          contacts.map((entry) => (
                            <tr key={entry.id}>
                              <td>
                                <BizInput
                                  theme="lac"
                                  name={`contacts[${entry.id}][name]`}
                                  onChange={(value) => { this.handleArrayFormValue('contacts', entry.id, 'name', value); }}
                                  value={entry.name}
                                />
                              </td>
                              <td>
                                <BizInput
                                  theme="lac"
                                  name={`contacts[${entry.id}][mobile]`}
                                  onChange={(value) => { this.handleArrayFormValue('contacts', entry.id, 'mobile', value); }}
                                  value={entry.mobile}
                                />
                              </td>
                              <td>
                                <BizInput
                                  theme="lac"
                                  name={`contacts[${entry.id}][email]`}
                                  onChange={(value) => { this.handleArrayFormValue('contacts', entry.id, 'email', value); }}
                                  value={entry.email}
                                />
                              </td>
                              <td>
                                <BizInput
                                  theme="lac"
                                  name={`contacts[${entry.id}][designation]`}
                                  onChange={(value) => { this.handleArrayFormValue('contacts', entry.id, 'designation', value); }}
                                  value={entry.designation}
                                />
                              </td>
                              <td>
                                <DeleteFormRowButton onClick={() => { this.removeFormRow('contacts', entry.id); }} />
                              </td>
                            </tr>
                          ))
                        }
                      </tbody>
                    </table>
                    :
                    null
                }
              </td>
              <td className="biz-form-table-action">
                <Button
                  onClick={() => {
                    this.setState({
                      contacts: [...contacts, {
                        id: (+new Date()).toString(36),
                        name: '',
                        mobile: '',
                        email: '',
                        designation: '',
                      }],
                    });
                  }}
                >
                  Add new contact
                </Button>
              </td>
            </tr>

            {/* Proposal Brief */}
            <tr>
              <td className="biz-form-table-label">Proposal Brief</td>
              <td className="biz-form-table-input">
                <BizInput
                  type="textarea"
                  theme="lac"
                  name="proposalBrief"
                  onChange={(value) => { this.setState({ proposalBrief: value }); }}
                  value={proposalBrief}
                  style={{ width: '100%', height: 150, maxWidth: 'unset' }}
                />
              </td>
              <td className="biz-form-table-action" />
            </tr>

            {/* Financial Summary */}
            <tr>
              <td className="biz-form-table-label">Financial Summary</td>
              <td className="biz-form-table-input">
                <table className="biz-data-table biz-bordered">
                  <thead>
                    <tr>
                      <th>Particulars</th>
                      <th>
                        <div style={{ width: 100 }}>
                          <BizSelect
                            options={this.createOptionsFromList(yearsList)}
                            theme="lac"
                            onChange={(value) => { this.setState({ financialSummaryYears: [value, financialSummaryYears[1], financialSummaryYears[2]] }); }}
                            name="financialSummaryYears[0]"
                            value={financialSummaryYears[0]}
                            placeholder="Year"
                          />
                        </div>
                      </th>
                      <th>
                        <div style={{ width: 100 }}>
                          <BizSelect
                            options={this.createOptionsFromList(yearsList)}
                            theme="lac"
                            onChange={(value) => { this.setState({ financialSummaryYears: [financialSummaryYears[0], value, financialSummaryYears[2]] }); }}
                            name="financialSummaryYears[1]"
                            value={financialSummaryYears[1]}
                            placeholder="Year"
                          />
                        </div>
                      </th>
                      <th>
                        <div style={{ width: 100 }}>
                          <BizSelect
                            options={this.createOptionsFromList(yearsList)}
                            theme="lac"
                            onChange={(value) => { this.setState({ financialSummaryYears: [financialSummaryYears[0], financialSummaryYears[1], value] }); }}
                            name="financialSummaryYears[2]"
                            value={financialSummaryYears[2]}
                            placeholder="Year"
                          />
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {
                      Object.keys(leadFinancialSummaryNames).map((key) => (
                        <tr key={key}>
                          <td>{leadFinancialSummaryNames[key]}</td>
                          <td>
                            <BizInput
                              type="number"
                              name={`financialSummary[${key}][0]`}
                              theme="lac"
                              onChange={(value) => {
                                const arr = financialSummary[key];
                                this.setState({ financialSummary: { ...financialSummary, [key]: [value, arr[1], arr[2]] } });
                              }}
                              value={financialSummary[key][0]}
                            />
                          </td>
                          <td>
                            <BizInput
                              type="number"
                              name={`financialSummary[${key}][1]`}
                              theme="lac"
                              onChange={(value) => {
                                const arr = financialSummary[key];
                                this.setState({ financialSummary: { ...financialSummary, [key]: [arr[0], value, arr[2]] } });
                              }}
                              value={financialSummary[key][1]}
                            />
                          </td>
                          <td>
                            <BizInput
                              type="number"
                              name={`financialSummary[${key}][2]`}
                              theme="lac"
                              onChange={(value) => {
                                const arr = financialSummary[key];
                                this.setState({ financialSummary: { ...financialSummary, [key]: [arr[0], arr[1], value] } });
                              }}
                              value={financialSummary[key][2]}
                            />
                          </td>
                        </tr>
                      ))
                    }
                  </tbody>
                </table>
              </td>
              <td className="biz-form-table-action" />
            </tr>

            {/* Landline */}
            <tr>
              <td className="biz-form-table-label">Landline no.</td>
              <td className="biz-form-table-input">
                <BizInput
                  name="landline"
                  theme="lac"
                  onChange={(value) => { this.setState({ landline: value }); }}
                  value={landline}
                />
              </td>
              <td className="biz-form-table-action" />
            </tr>

            {/* Company Email */}
            <tr>
              <td className="biz-form-table-label">Company email id</td>
              <td className="biz-form-table-input">
                <BizInput
                  name="companyEmail"
                  theme="lac"
                  onChange={(value) => { this.setState({ companyEmail: value }); }}
                  value={companyEmail}
                />
              </td>
              <td className="biz-form-table-action" />
            </tr>

            {/* Website */}
            <tr>
              <td className="biz-form-table-label">Website</td>
              <td className="biz-form-table-input">
                <BizInput
                  name="website"
                  theme="lac"
                  onChange={(value) => { this.setState({ website: value }); }}
                  value={website}
                />
              </td>
              <td className="biz-form-table-action" />
            </tr>

            {/* Project Owner */}
            <tr>
              <td className="biz-form-table-label">Assign a project owner</td>
              <td className="biz-form-table-input">
                <BizSelect
                  options={employees.map((employee) => ({ label: employee.name, value: employee.id }))}
                  theme="lac"
                  onChange={(value) => { this.setState({ projectOwnerId: value }); }}
                  name="projectOwnerId"
                  value={projectOwnerId}
                />
              </td>
              <td className="biz-form-table-action" />
            </tr>

            {/* Referred By - Associate */}
            <tr>
              <td className="biz-form-table-label">Referred by - Associate</td>
              <td className="biz-form-table-input">
                <BizSelect
                  options={associates.map((entry) => ({ label: entry.name, value: entry.id }))}
                  theme="lac"
                  onChange={(value) => { this.setState({ referredByAssociateId: value }); }}
                  name="referredByAssociateId"
                  value={referredByAssociateId}
                />
              </td>
              <td className="biz-form-table-action">
                <Button
                  onClick={() => {
                    this.setState({
                      additionalStates: {
                        ...additionalStates,
                        associateCreateDialog: true,
                      },
                    });
                  }}
                >
                  Add new associate
                </Button>
              </td>
            </tr>

            {/* Referred By - Employee */}
            <tr>
              <td className="biz-form-table-label">Referred by - Employee</td>
              <td className="biz-form-table-input">
                <BizSelect
                  options={employees.map((employee) => ({ label: employee.name, value: employee.id }))}
                  theme="lac"
                  onChange={(value) => { this.setState({ referredByEmployeeId: value }); }}
                  name="referredByEmployeeId"
                  value={referredByEmployeeId}
                />
              </td>
              <td className="biz-form-table-action" />
            </tr>

            <tr>
              <td className="biz-form-table-label" />
              <td colSpan={2}>
                <MuiButton variant="raised" color="primary" style={{ margin: '5px 0' }} onClick={this.handleFormSubmit}>
                  Submit
                </MuiButton>
              </td>
            </tr>
          </tbody>
        </table>
      </BizCard>
    );
  }
}

LeadCreate.propTypes = {
  data: PropTypes.shape({
    loading: PropTypes.bool.isRequired,
    employees: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
    })),
    lists: PropTypes.object,
    associates: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
    })),
  }).isRequired,
  mutate: PropTypes.func.isRequired,
};

export default compose(
  graphql(QUERY),
  graphql(MUTATION),
)(LeadCreate);



// WEBPACK FOOTER //
// ./src/pages/leads/LeadCreate.jsx